import React from 'react';
import { Box, Typography, Paper, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Done, DoneAll } from '@mui/icons-material';
import moment from 'moment';
import '../../styles/custom.css';

const StyledMessageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isOwn'
})(({ theme, isOwn }) => ({
  display: 'flex',
  justifyContent: isOwn ? 'flex-end' : 'flex-start',
  marginBottom: theme.spacing(1.5),
  padding: theme.spacing(0, 1),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0, 0.5),
    marginBottom: theme.spacing(1),
  }
}));

const StyledMessageContent = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isOwn'
})(({ theme, isOwn }) => ({
  padding: theme.spacing(1.5, 2),
  maxWidth: '70%',
  borderRadius: '18px',
  background: isOwn 
    ? theme.palette.primary.main 
    : theme.palette.mode === 'dark' 
      ? theme.palette.background.paper
      : '#ffffff',
  color: isOwn 
    ? theme.palette.primary.contrastText 
    : theme.palette.text.primary,
  boxShadow: isOwn 
    ? '0 4px 15px rgba(25, 118, 210, 0.15)' 
    : theme.palette.mode === 'dark'
      ? '0 2px 10px rgba(255, 255, 255, 0.05)'
      : '0 2px 10px rgba(0, 0, 0, 0.05)',
  position: 'relative',
  backdropFilter: 'blur(10px)',
  border: isOwn 
    ? 'none' 
    : `1px solid ${theme.palette.divider}`,
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: 12,
    [isOwn ? 'right' : 'left']: -6,
    width: 12,
    height: 12,
    background: isOwn 
      ? theme.palette.primary.main 
      : theme.palette.mode === 'dark'
        ? theme.palette.background.paper
        : '#ffffff',
    transform: 'rotate(45deg)',
    borderLeft: isOwn 
      ? 'none' 
      : `1px solid ${theme.palette.divider}`,
    borderBottom: isOwn 
      ? 'none' 
      : `1px solid ${theme.palette.divider}`,
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '85%',
    padding: theme.spacing(1, 1.5),
    fontSize: '0.9rem',
  }
}));

const MediaContainer = styled(Box)(({ theme }) => ({
  maxWidth: '100%',
  marginBottom: '8px',
  borderRadius: '12px',
  overflow: 'hidden',
  position: 'relative',
  '&::before': {
    content: '""',
    display: 'block',
    paddingTop: '56.25%',
  },
  '& img, & video': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255,255,255,0.03)' 
      : 'rgba(0,0,0,0.03)',
    borderRadius: '12px',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'scale(1.02)',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 4px 20px rgba(255, 255, 255, 0.1)'
        : '0 4px 20px rgba(0, 0, 0, 0.1)'
    }
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: '4px',
    '&::before': {
      paddingTop: '75%',
    }
  }
}));

const TimeStamp = styled(Typography)(({ theme, isOwn }) => ({
  fontSize: '0.75rem',
  opacity: 0.9,
  marginTop: theme.spacing(0.5),
  textAlign: 'right',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(0.5),
  color: isOwn 
    ? theme.palette.primary.contrastText 
    : theme.palette.text.secondary,
  '& .MuiSvgIcon-root': {
    fontSize: '1rem',
    opacity: 0.8
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.7rem',
    marginTop: theme.spacing(0.25),
  }
}));

const MessageBubble = ({ message, isOwn }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const renderMedia = () => {
    if (!message.media) return null;

    if (message.media.type === 'image') {
      return (
        <MediaContainer>
          <img
            src={message.media.url}
            alt="Message attachment"
            onError={(e) => {
              console.log('Error loading image:', {
                url: message.media.url,
                error: e.error,
                target: e.target,
              });
            }}
          />
        </MediaContainer>
      );
    }

    if (message.media.type === 'video') {
      return (
        <MediaContainer>
          <video
            controls
            src={message.media.url}
            onError={(e) => {
              console.log('Error loading video:', {
                url: message.media.url,
                error: e.error,
                target: e.target,
              });
            }}
          />
        </MediaContainer>
      );
    }

    return null;
  };

  return (
    <StyledMessageContainer isOwn={isOwn} className="message-bubble">
      <StyledMessageContent isOwn={isOwn}>
        {renderMedia()}
        {message.content && (
          <Typography 
            variant={isMobile ? "body2" : "body1"} 
            component="div" 
            sx={{ 
              wordBreak: 'break-word',
              lineHeight: 1.4,
              '& a': {
                color: isOwn 
                  ? theme.palette.primary.contrastText 
                  : theme.palette.primary.main,
                textDecoration: 'underline',
              }
            }}
          >
            {message.content}
          </Typography>
        )}
        <TimeStamp 
          variant="caption" 
          component="div"
          isOwn={isOwn}
        >
          {moment(message.createdAt).format('HH:mm')}
          {isOwn && (
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {message.read ? <DoneAll fontSize="small" /> : <Done fontSize="small" />}
            </Box>
          )}
        </TimeStamp>
      </StyledMessageContent>
    </StyledMessageContainer>
  );
};

export default MessageBubble; 